import imgHub from "../../assets/70ea0b2ec231dcd94ed18a320a7942263712c731.png";
import { useEffect, useMemo, useState } from "react";
import { opEdsData } from "../../data/opeds.ts";

function ArticleLogo({ link }: { link: string }) {
  const isNationalPost = link.includes("nationalpost.com");
  const isTorontoStar = link.includes("thestar.com");

  if (isNationalPost) {
    return (
      <div className="h-[33px] w-[28px] flex-shrink-0">
        <img alt="National Post" src="/nplogo 1.png" className="w-full h-full object-contain" />
      </div>
    );
  }

  if (isTorontoStar) {
    return (
      <div className="h-[33px] w-[28px] flex-shrink-0">
        <img alt="Toronto Star" src="/t_star.jpg" className="w-full h-full object-contain" />
      </div>
    );
  }

  return (
    <div className="inline-grid grid-cols-[max-content] grid-rows-[max-content] leading-[0]">
      <div className="h-[33px] w-[28px] relative">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <img alt="The Hub" className="absolute h-[122.37%] left-[-53.78%] max-w-none top-[-9.57%] w-[329.79%]" src={imgHub} />
        </div>
      </div>
    </div>
  );
}

function OpEdCard({ title, author, link }: { title: string; author: string; link: string }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className={`flex-1 min-w-[260px] rounded-[12px] transition-colors duration-300 ${hover ? "bg-white" : "bg-[#00499d]"}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="p-[24px] flex flex-col h-full">
        <ArticleLogo link={link} />
        <div className={`mt-4 font-['Geist',_sans-serif] text-[16px] md:text-[18px] lg:text-[20px] tracking-[-0.48px] transition-colors flex-grow ${hover ? "text-[#00499d]" : "text-white"}`}>
          <span className="font-bold">{author}</span>
          <span className="font-bold">: </span>
          <span className="font-normal">{title}</span>
        </div>
        <button
          onClick={() => window.open(link, "_blank")}
          className={`underline transition-colors text-left mt-4 ${hover ? "text-[#00499d]" : "text-white"}`}
        >
          Read more
        </button>
      </div>
    </div>
  );
}

export default function OpEdSection() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mm = window.matchMedia("(max-width: 767px)");
    const onChange = (e: any) => setIsMobile(e.matches);
    setIsMobile(mm.matches);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore legacy support
    mm.addEventListener ? mm.addEventListener("change", onChange) : mm.addListener(onChange);
    return () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore legacy support
      mm.removeEventListener ? mm.removeEventListener("change", onChange) : mm.removeListener(onChange);
    };
  }, []);

  const itemsPerGroup = isMobile ? 1 : 3;
  const sortedOpEds = useMemo(() => {
    return [...opEdsData].sort(
      (a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, []);
  const groups = useMemo(() => {
    const arr: any[] = [];
    for (let i = 0; i < sortedOpEds.length; i += itemsPerGroup) {
      arr.push(sortedOpEds.slice(i, i + itemsPerGroup));
    }
    return arr;
  }, [itemsPerGroup, sortedOpEds]);

  const [groupIndex, setGroupIndex] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right' | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (groupIndex >= groups.length) setGroupIndex(0);
  }, [groups.length, groupIndex]);

  const prev = () => {
    if (isAnimating) return;
    setDirection('right');
    setIsAnimating(true);
    setTimeout(() => {
      setGroupIndex((i) => (i + groups.length - 1) % groups.length);
      setIsAnimating(false);
    }, 300);
  };

  const next = () => {
    if (isAnimating) return;
    setDirection('left');
    setIsAnimating(true);
    setTimeout(() => {
      setGroupIndex((i) => (i + 1) % groups.length);
      setIsAnimating(false);
    }, 300);
  };

  return (
    <section id="opeds" className="bg-[#003c81] relative w-full py-10 md:py-16 lg:py-[100px]" data-name="Op-Ed">
      <div className="flex flex-col justify-center w-full">
        <div className="box-border content-stretch flex flex-col gap-[20px] md:gap-[24px] items-center justify-center relative w-full">
          <div className="max-w-[1200px] w-full mx-auto">
            <p className="px-4 md:px-8 lg:px-0 font-['Geist:Bold',_sans-serif] font-bold leading-[normal] relative shrink-0 text-[20px] md:text-[24px] text-white tracking-[-0.48px] w-full mb-6 md:mb-10">Op-Eds</p>
            <div className="overflow-hidden px-4 md:px-0">
              <div
                className={`grid grid-cols-1 md:grid-cols-3 gap-[10px] transition-all duration-300 ease-in-out ${
                  isAnimating
                    ? direction === 'left'
                      ? 'opacity-0 -translate-x-12'
                      : 'opacity-0 translate-x-12'
                    : 'opacity-100 translate-x-0'
                }`}
              >
                {groups.length > 0 && groups[groupIndex].map((item: any) => (
                  <OpEdCard key={item.id} title={item.title} author={item.author} link={item.link} />
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-4 mt-2">
            <button onClick={prev} aria-label="Previous" className="p-2 hover:opacity-80 transition-opacity">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="flex items-center gap-2">
              {groups.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    if (isAnimating || i === groupIndex) return;
                    setDirection(i > groupIndex ? 'left' : 'right');
                    setIsAnimating(true);
                    setTimeout(() => {
                      setGroupIndex(i);
                      setIsAnimating(false);
                    }, 300);
                  }}
                  className="h-2.5 w-2.5 flex items-center justify-center"
                >
                  {i === groupIndex ? (
                    <svg width="10" height="10" viewBox="0 0 10 10" className="drop-shadow-sm"><circle cx="5" cy="5" r="5" fill="white" /></svg>
                  ) : (
                    <svg width="10" height="10" viewBox="0 0 10 10"><circle cx="5" cy="5" r="4.5" stroke="white" /></svg>
                  )}
                </button>
              ))}
            </div>
            <button onClick={next} aria-label="Next" className="p-2 hover:opacity-80 transition-opacity">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}



